import { baseApi } from '../../services/baseApi';
import { ApiEndPoints } from '../../constants/apiEndPoints';

/** A file attached to a daily direction (as posted, and as read back). */
export interface DirectionAsset {
  id?: number;
  file_url?: string;
  file_type?: string;
  name?: string;
}

/**
 * A daily direction. The POST body uses camelCase (title, oneLine, dueDate…); the read
 * shape may echo those or a snake_case variant, so values are read via the accessors.
 */
export interface DailyDirection {
  id: number;
  /** Human code, e.g. "DD-1023". */
  dd_code?: string | null;
  // Title — the read model uses `name`; the write payload uses `title`.
  title?: string | null;
  name?: string | null;
  oneLine?: string | null;
  one_line?: string | null;
  description?: string | null;
  priority?: string | null;
  status?: string | null;
  // Due date — read: `direction_date`; write: `dueDate`.
  dueDate?: string | null;
  due_date?: string | null;
  direction_date?: string | null;
  dueTime?: string | null;
  due_time?: string | null;
  duration?: string | null;
  repeat?: boolean | null;
  is_recurring?: boolean | null;
  // Recurrence — read: `recurrence_type` / `recurrence_start_date` / `recurrence_end_date`.
  frequency?: string | null;
  recurrence_type?: string | null;
  // Monthly "Repeat Pattern" (e.g. "First Saturday") — read camelCase or snake_case.
  monthlyPattern?: string | null;
  monthly_pattern?: string | null;
  recurrence_pattern?: string | null;
  // Weekly "Repeats On" weekday (e.g. "Monday").
  weekday?: string | null;
  recurrence_weekday?: string | null;
  startDate?: string | null;
  start_date?: string | null;
  recurrence_start_date?: string | null;
  endDate?: string | null;
  end_date?: string | null;
  recurrence_end_date?: string | null;
  ec_location_level_id?: number | null;
  area_code?: string | null;
  assignees?: Array<number | { id?: number; full_name?: string; name?: string }> | null;
  assets?: DirectionAsset[] | null;
  reference_person?: {
    scope?: string | null;
    /** Which source list the id came from ('directory' | 'user'). */
    source?: string | null;
    /** Reference-person id (scope "inside"). */
    user?: number | string | null;
    /** Typed name (outside / NCR). */
    name?: string | null;
    phone?: string | null;
    /** One or more contact numbers. */
    phones?: string[] | null;
    designation?: string | null;
    location?: string | null;
  } | null;
  /** Resolved reference-person name + phone (list response). */
  reference_person_name?: string | null;
  reference_person_phone?: string | null;
  created_at?: string | null;
}

/** Request body for POST /daily-direction/. */
export interface CreateDirectionPayload {
  title: string;
  oneLine?: string;
  description?: string;
  priority?: string;
  /** Schedule start (server maps to direction_date / due_time). */
  startingDate?: string;
  startingTime?: string;
  /** Schedule end (server maps to end_date). Defaults to start + 7 days in the UI. */
  endingDate?: string;
  dueDate?: string;
  dueTime?: string;
  duration?: string;
  repeat?: boolean;
  frequency?: string;
  /** Monthly "Repeat Pattern", e.g. "First Saturday". */
  monthlyPattern?: string;
  /** Weekly "Repeats On" weekday, e.g. "Monday". */
  weekday?: string;
  startDate?: string;
  endDate?: string;
  assignees?: number[];
  assets?: DirectionAsset[];
  referencePerson?: {
    scope: string; // 'inside' | 'outside' | 'delhi'
    /** Within constituency → reference-person id. */
    user?: number;
    /** Which source list the id came from ('directory' | 'user'). */
    source?: string;
    /** Outside / NCR → typed name + contact. */
    name?: string;
    phone?: string;
    /** All entered contact numbers. */
    phones?: string[];
    designation?: string;
    location?: string;
  };
  status?: string;
}

/**
 * A selectable reference person (GET /reference-persons/). The list mixes two sources
 * (`directory` people and app `user`s); `id` is only unique *within* a source, so the
 * server returns `key` = "<source>-<id>" (e.g. "directory-2", "user-21") — use that as
 * the dropdown's unique value.
 */
export interface ReferencePerson {
  id: number;
  key?: string | null;
  source?: string | null;
  name?: string | null;
  full_name?: string | null;
  designation?: string | null;
  department?: string | null;
  phone?: string | null;
  /** All contact numbers (the API returns `phone` as a comma-joined string too). */
  phones?: string[] | null;
  location?: string | null;
}
/** A person's contact numbers as a clean list (prefers `phones`, falls back to `phone`). */
export const referencePersonPhones = (p: ReferencePerson): string[] =>
  (p.phones ?? (p.phone ? p.phone.split(',') : [])).map((s) => s.trim()).filter(Boolean);
export const referencePersonName = (p: ReferencePerson) =>
  p.name || p.full_name || `Person ${p.id}`;
/** Unique composite value — the server's `key`, or a "<source>-<id>" fallback. */
export const referencePersonKey = (p: ReferencePerson) => p.key || `${p.source ?? 'directory'}-${p.id}`;

/** Daily-direction summary counts (GET /daily-direction/stats/). */
export interface DirectionStats {
  all?: number;
  to_do?: number;
  done?: number;
  closed?: number;
  doing?: number;
  [key: string]: number | undefined;
}

/** DRF list responses may be a plain array or a paginated `{ results }` envelope. */
type Listish<T> = T[] | { count?: number; results?: T[] };
const asList = <T>(r: Listish<T>): T[] => (Array.isArray(r) ? r : (r?.results ?? []));

/** Normalized list result: current page rows plus the server's total count. */
export interface Paginated<T> {
  items: T[];
  total: number;
}
const toPaginated = <T>(r: Listish<T>): Paginated<T> => {
  const items = asList(r);
  const total = Array.isArray(r) ? r.length : (r?.count ?? items.length);
  return { items, total };
};

/* ------------------------------------------------------------------ *
 * Defensive accessors — exact field names aren't guaranteed.
 * ------------------------------------------------------------------ */
export const dirCode = (d: DailyDirection) => d.dd_code || `DD-${d.id}`;
export const dirTitle = (d: DailyDirection) => d.title || d.name || 'Untitled direction';
export const dirOneLine = (d: DailyDirection) => d.oneLine || d.one_line || '';
export const dirDescription = (d: DailyDirection) => d.description || '';
export const dirPriority = (d: DailyDirection) => d.priority || '';
export const dirRawStatus = (d: DailyDirection) => d.status || '';
export const dirDueDate = (d: DailyDirection) => d.dueDate || d.due_date || d.direction_date || '';
export const dirDueTime = (d: DailyDirection) => d.dueTime || d.due_time || '';
export const dirDuration = (d: DailyDirection) => d.duration || '';
export const dirFrequency = (d: DailyDirection) =>
  (d.frequency || d.recurrence_type || '').toLowerCase();
export const dirMonthlyPattern = (d: DailyDirection) =>
  d.monthlyPattern || d.monthly_pattern || d.recurrence_pattern || '';
export const dirWeekday = (d: DailyDirection) => d.weekday || d.recurrence_weekday || '';
export const dirStartDate = (d: DailyDirection) =>
  d.startDate || d.start_date || d.recurrence_start_date || '';
export const dirEndDate = (d: DailyDirection) =>
  d.endDate || d.end_date || d.recurrence_end_date || '';
export const dirIsRecurring = (d: DailyDirection): boolean =>
  Boolean(d.repeat ?? d.is_recurring ?? false);
export const dirAssets = (d: DailyDirection): DirectionAsset[] =>
  Array.isArray(d.assets) ? d.assets : [];
export const dirAssigneeIds = (d: DailyDirection): number[] =>
  (d.assignees ?? [])
    .map((a) => (typeof a === 'number' ? a : a?.id))
    .filter((v): v is number => typeof v === 'number');
export const dirAssigneeNames = (d: DailyDirection): string[] =>
  (d.assignees ?? [])
    .map((a) => (typeof a === 'number' ? '' : a?.full_name || a?.name || ''))
    .filter((v): v is string => Boolean(v));
export const dirReferencePerson = (d: DailyDirection) => d.reference_person ?? null;
export const dirReferencePersonName = (d: DailyDirection) => d.reference_person_name || '';
export const dirReferencePersonPhone = (d: DailyDirection) => d.reference_person_phone || '';

/** Normalize a free-form direction status into the UI status keys used by the design. */
export type UiDirStatus = 'completed' | 'inprogress' | 'pending' | 'assigned';
export const dirStatusKey = (raw: string): UiDirStatus => {
  const s = raw.toLowerCase().replace(/[\s_-]+/g, '');
  if (s.includes('complet')) return 'completed';
  if (s.includes('progress')) return 'inprogress';
  if (s.includes('assign')) return 'assigned';
  return 'pending';
};

export const dailyDirectionApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getDirections: build.query<
      Paginated<DailyDirection>,
      {
        search?: string;
        priority?: string;
        isRecurring?: boolean;
        status?: string;
        page?: number;
        pageSize?: number;
      } | void
    >({
      query: (arg) => ({
        url: `${ApiEndPoints.dailyDirection}/`,
        params: {
          ...(arg?.search ? { search: arg.search } : {}),
          ...(arg?.priority ? { priority: arg.priority } : {}),
          ...(arg?.isRecurring === true ? { is_recurring: true } : {}),
          ...(arg?.status ? { status: arg.status } : {}),
          page: arg?.page ?? 1,
          page_size: arg?.pageSize ?? 10,
        },
      }),
      transformResponse: (r: Listish<DailyDirection>) => toPaginated(r),
      providesTags: ['DailyDirections'],
    }),
    getDirection: build.query<DailyDirection, number | string>({
      query: (id) => ({ url: `${ApiEndPoints.dailyDirection}/${id}/` }),
      providesTags: ['DailyDirections'],
    }),
    createDirection: build.mutation<DailyDirection, CreateDirectionPayload>({
      query: (body) => ({ url: `${ApiEndPoints.dailyDirection}/`, method: 'POST', body }),
      invalidatesTags: ['DailyDirections'],
    }),
    updateDirection: build.mutation<
      DailyDirection,
      { id: number | string; patch: Partial<CreateDirectionPayload> }
    >({
      query: ({ id, patch }) => ({
        url: `${ApiEndPoints.dailyDirection}/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      // Refresh the direction lists AND its activity history (a status change is logged
      // server-side, so the Activity History panel refetches automatically).
      invalidatesTags: ['DailyDirections', 'ActivityLog'],
    }),
    deleteDirection: build.mutation<void, number | string>({
      query: (id) => ({ url: `${ApiEndPoints.dailyDirection}/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['DailyDirections'],
    }),
    // Summary counts for the list-page stat cards.
    getDirectionStats: build.query<DirectionStats, void>({
      query: () => ({ url: `${ApiEndPoints.dailyDirectionStats}/` }),
      providesTags: ['DailyDirections'],
    }),
    // Reference persons within an organization (the "Select Reference Person" dropdown).
    getReferencePersons: build.query<
      ReferencePerson[],
      { organization: number | string; search?: string }
    >({
      query: ({ organization, search }) => ({
        url: `${ApiEndPoints.referencePersons}/`,
        params: { organization, ...(search ? { search } : {}) },
      }),
      transformResponse: (r: Listish<ReferencePerson>) => asList(r),
    }),

    // Designations a reference person may hold (GET /reference-persons/designations/).
    // Normalises to a flat string[] whether the API returns strings or {name}/{label} objects.
    getReferencePersonDesignations: build.query<string[], void>({
      query: () => ({ url: `${ApiEndPoints.referencePersons}/designations/` }),
      transformResponse: (r: Listish<string | { name?: string; label?: string; value?: string }>) =>
        asList(r)
          .map((d) => (typeof d === 'string' ? d : (d?.name ?? d?.label ?? d?.value ?? '')))
          .filter(Boolean),
    }),
  }),
});

export const {
  useGetDirectionsQuery,
  useGetDirectionQuery,
  useCreateDirectionMutation,
  useUpdateDirectionMutation,
  useDeleteDirectionMutation,
  useGetDirectionStatsQuery,
  useGetReferencePersonsQuery,
  useGetReferencePersonDesignationsQuery,
} = dailyDirectionApi;
